import { APIKit } from "config/axios/index";
import store from "config/redux/index";
import { start, end } from "config/redux/slice";

import { ServerConstants, ServiceConstants } from "constant";
import { ServiceModel } from "models";
import {
  QuickCheckDocumentType,
  ChangeQuickCheckRequest,
} from "pages/clients/view/quick-check/types";

import { handleErrorWithTokenRenewal } from "./common";

const { API_V1_QUICK_CHECK } = ServerConstants;
const {
  REQUEST_QUICK_CHECK_LIST_ID,
  REQUEST_QUICK_CHECK_INDICATOR_ID,
  REQUEST_QUICK_CHECK_UPLOAD_ID,
  REQUEST_QUICK_CHECK_UPDATE_ID,
  REQUEST_QUICK_CHECK_DELETE_ID,
} = ServiceConstants;

/**
 * Dohvata sve Quick Check dokumente za klijenta
 */
export async function quickCheckList(clientId: number) {
  const requestId = REQUEST_QUICK_CHECK_LIST_ID + clientId;
  try {
    store.dispatch(start(requestId));
    const response = await APIKit.get(`${API_V1_QUICK_CHECK}?clientId=${clientId}`);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, quickCheckList, [clientId], requestId);
  }
}

/**
 * Dohvata samo ukupni status (GREEN/YELLOW/RED) za klijenta
 * Koristi se za brzi hover prikaz
 */
export async function quickCheckIndicator(clientId: number) {
  const requestId = REQUEST_QUICK_CHECK_INDICATOR_ID + clientId;
  try {
    store.dispatch(start(requestId));
    const response = await APIKit.get(`${API_V1_QUICK_CHECK}/indicator?clientId=${clientId}`);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, quickCheckIndicator, [clientId], requestId);
  }
}

/**
 * Upload novog Quick Check dokumenta
 */
export async function quickCheckUpload(clientId: number, type: QuickCheckDocumentType, file: File) {
  const requestId = REQUEST_QUICK_CHECK_UPLOAD_ID + clientId + type;
  try {
    store.dispatch(start(requestId));
    const formData = new FormData();
    formData.append("file", file);
    const response = await APIKit.post(
      `${API_V1_QUICK_CHECK}/client/${clientId}/${type}`,
      formData,
      {
        headers: {
          ...APIKit.defaults.headers["common"],
          "Accept-Language": APIKit.defaults.headers["Accept-Language"],
          "Content-Type": "multipart/form-data",
        },
      },
    );
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, quickCheckUpload, [clientId, type, file], requestId);
  }
}

/**
 * Izmena Quick Check dokumenta (status, points, blockedUntil)
 */
export async function quickCheckUpdate(id: string, data: ChangeQuickCheckRequest) {
  const requestId = REQUEST_QUICK_CHECK_UPDATE_ID + id;
  try {
    store.dispatch(start(requestId));
    const response = await APIKit.put(`${API_V1_QUICK_CHECK}/${id}`, data);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, quickCheckUpdate, [id, data], requestId);
  }
}

/**
 * Brisanje Quick Check dokumenta
 */
export async function quickCheckDelete(id: string) {
  const requestId = REQUEST_QUICK_CHECK_DELETE_ID + id;
  try {
    store.dispatch(start(requestId));
    const response = await APIKit.delete(`${API_V1_QUICK_CHECK}/${id}`);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, quickCheckDelete, [id], requestId);
  }
}

/**
 * Download Quick Check dokumenta
 * Preuzima dokument sa pravilnim imenom fajla
 */
export async function quickCheckDownload(
  quickCheckId: string,
  fileName: string,
  extension: string,
) {
  try {
    const response = await APIKit.get(`${API_V1_QUICK_CHECK}/${quickCheckId}/download`, {
      responseType: "blob",
    });

    // Kreiraj blob URL i preuzmi sa pravilnim imenom
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const a = document.createElement("a");
    a.href = url;
    a.download = `${fileName}.${extension}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  } catch (err: any) {
    console.error("Download failed:", err);
    throw err;
  }
}

/**
 * View Quick Check dokumenta
 * Otvara dokument u novom browser tabu
 */
export async function quickCheckView(quickCheckId: string) {
  try {
    const response = await APIKit.get(`${API_V1_QUICK_CHECK}/${quickCheckId}/download`, {
      responseType: "blob",
    });

    // Kreiraj blob URL i otvori u novom tabu
    const blob = new Blob([response.data], { type: "application/pdf" });
    const url = window.URL.createObjectURL(blob);
    window.open(url, "_blank");

    // Cleanup blob URL nakon kratkog delay-a
    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
  } catch (err: any) {
    console.error("View failed:", err);
    throw err;
  }
}
